/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import {
  Box,
  IconButton,
  List,
  MenuItem as MuiMenuItem,
  Stack,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import MaterialSymbol from "../../../components/MaterialSymbol";
import Modal from "../../../components/Modal";
import { PadLineItem } from "../../../components/Pad/Pad";
import StatusChip from "../../../components/StatusChip";
import TextField from "../../../components/TextField";
import Typography from "../../../components/Typography";
import YesNoAction from "../../../components/YesNoAction";
import { useAppDispatch } from "../../../store/rootStore";
import { TestIds } from "../../../testIds";
import { PRODUCTS_CART_ROUTE } from "../../products/constants";
import AuthSlice, { BasicDigitalFarmerAccount } from "../storage";

import { useAccountSwitcher } from "./AccountSwitcherProvider";

const MenuItem = styled(MuiMenuItem)({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  margin: "0 -16px",
  ":hover": {},
});

interface Props {
  closeAccountSwitcher: () => void;
}

export const AccountMenuItem: React.FC<{
  selected?: boolean;
  showEmail?: boolean;
  bolden: boolean | undefined;
  item: BasicDigitalFarmerAccount;
  onChange: (event: React.SyntheticEvent<Element, Event>, value: string) => void;
}> = ({ selected, showEmail, bolden, item, onChange }) => (
  <MenuItem
    key={item.accountName}
    data-testid={TestIds.components.accountSwitch.menuItem}
    onClick={(event) => onChange(event, item.accountNumber)}
    sx={{
      height: "auto",
      borderRadius: 2,
      margin: 1,
      background: colors.primary50,
      position: "relative",
    }}
  >
    <Stack direction="row" spacing={2} maxWidth={"100%"} width="100%">
      <Box maxWidth={"100%"} width="100%">
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent={"space-between"}
          maxWidth={"100%"}
          width="100%"
        >
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {item.accountName}
          </Typography>

          {bolden && (
            <StatusChip
              bgColor={colors.primary50}
              textColor="black"
              label="TFO"
              sx={{ float: "right" }}
              disableHover
              data-testid={TestIds.components.accountSwitch.chip}
            />
          )}
        </Stack>

        {showEmail && (
          <Typography
            variant="body2"
            fontWeight={400}
            sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {item.accountEmail || "No contact email address"}
          </Typography>
        )}
        <Typography variant="body2" color="grey700">
          {item.accountNumber}
        </Typography>
      </Box>
      {selected && (
        <Box position={"absolute"} right="15px" top="30px">
          <MaterialSymbol>check</MaterialSymbol>
        </Box>
      )}
    </Stack>
  </MenuItem>
);

export const AccountPadItem: React.FC<{
  item: BasicDigitalFarmerAccount;
  onChange: (event: React.SyntheticEvent<Element, Event>, value: string) => void;
}> = ({ item, onChange }) => (
  <PadLineItem
    data-testid={TestIds.components.accountSwitch.menuItem}
    key={item.accountName}
    option={{
      title: item.accountName,
      subtitle: item.accountNumber,
    }}
    onClick={(event) => onChange(event, item.accountNumber)}
  />
);

export const AccountItem = ({
  item,
  bolden,
  onChange,
}: {
  item: BasicDigitalFarmerAccount;
  bolden?: boolean;
  onChange: (event: React.SyntheticEvent<Element, Event>, value: string) => void;
}) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("md"));

  return isMd ? (
    <AccountPadItem item={item} onChange={onChange} />
  ) : (
    <AccountMenuItem bolden={bolden} item={item} onChange={onChange} />
  );
};

const AccountSwitcher: React.FC<Props> = ({ closeAccountSwitcher }) => {
  const {
    filteredAccounts,
    recentViews,
    searchText,
    searchEnabled,
    openAlertModal,
    switchId,
    handleOnSearch,
    onChange,
    setOpenAlertModal,
  } = useAccountSwitcher();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const renderRecentViews = React.useMemo(() => {
    if (searchText) {
      return null;
    }
    if (recentViews.length === 0) {
      return null;
    }
    return (
      <Box data-testid={TestIds.components.accountSwitch.recentViews}>
        <Typography variant="body2" color="primary500" fontWeight={"bold"} py={2}>
          Recently viewed
        </Typography>
        {recentViews.map((account) => (
          <AccountItem
            bolden={account.hasTfoAccess}
            item={account}
            onChange={(event, value) => {
              if (onChange(event, value)) {
                closeAccountSwitcher();
              }
            }}
            key={account.accountNumber + "-recent-view"}
          />
        ))}
      </Box>
    );
  }, [recentViews, searchText]);

  const renderAccounts = React.useMemo(() => {
    let prevGroup = "";

    return filteredAccounts.map((account) => {
      const currentGroup = account.accountName.charAt(0);
      const showGroup = currentGroup !== prevGroup;
      prevGroup = currentGroup;

      return (
        <List key={account.accountNumber} sx={{ p: 0, gap: 0 }}>
          {showGroup && !searchText && (
            <Typography variant="body2" color="primary500" fontWeight={"bold"} py={2}>
              {currentGroup}
            </Typography>
          )}
          <AccountItem
            bolden={account.hasTfoAccess}
            item={account}
            onChange={(event, value) => {
              if (onChange(event, value)) {
                closeAccountSwitcher();
              }
            }}
            key={account.accountNumber}
          />
        </List>
      );
    });
  }, [filteredAccounts, searchText]);

  const renderAlertModal = React.useMemo(
    () => (
      <Modal
        title="Switching account"
        handleClose={() => setOpenAlertModal(false)}
        open={openAlertModal}
        data-testid={TestIds.components.accountSwitch.alertModal}
      >
        <YesNoAction
          body={`Please note that when you switch accounts, the items currently in your cart will not be transferred. However, we will save those items for you, so they'll be waiting in your cart for when you return.`}
          buttonBoxProps={{
            reverse: true,
            sm: {
              flexGrow: 1,
              marginTop: "16px",
            },
          }}
          optionA={{
            label: "Switch Account",
            testId: `${TestIds.components.accountSwitch.switchAccountButton}`,
            onClick: () => {
              if (switchId) {
                onChange(undefined, switchId);
                dispatch(AuthSlice.actions.setSelectedFarmerAccount(switchId));
              }
              setOpenAlertModal(false);
              closeAccountSwitcher();
            },
          }}
          optionB={{
            label: "View cart",
            testId: `${TestIds.components.accountSwitch.viewCart}`,
            onClick: () => {
              closeAccountSwitcher();
              navigate(PRODUCTS_CART_ROUTE);
            },
          }}
        />
      </Modal>
    ),
    [openAlertModal, setOpenAlertModal, switchId, closeAccountSwitcher]
  );

  return (
    <Box data-testid={TestIds.components.accountSwitch.switcher}>
      {searchEnabled && (
        <TextField
          value={searchText}
          placeholder="Search your account"
          testId={TestIds.components.accountSwitch.searchBar}
          variant={"outlined"}
          onChange={handleOnSearch}
          inputProps={{
            maxLength: 200,
          }}
          autoFocus={true}
          sx={{ height: "45px", pr: "3px", mb: 2 }}
          InputProps={{
            endAdornment: (
              <IconButton color="primary" title="Search">
                <MaterialSymbol>search</MaterialSymbol>
              </IconButton>
            ),
          }}
        />
      )}
      {renderRecentViews}
      {renderAccounts}
      {renderAlertModal}
    </Box>
  );
};

export default AccountSwitcher;
